import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Search, CheckCircle2, ScanLine, RefreshCcw, Clock, User, X,
} from "lucide-react";

/**
 * Event-Day Check-In (Display America staff only).
 * - Search by name / company / email / Check-In ID (full or first 8 chars).
 * - "Scan" mode: dedicated field for barcode/QR scanners — hits Enter, we auto check-in.
 * - Marks attendee checked-in via POST /api/admin/attendees/checkin — the backend records
 *   checked_in_at + checked_in_by (the operator's email).
 */
const TYPE_LABELS = {
  prime_supplier: "Prime Supplier",
  prime_additional_rep: "Prime · Add'l Rep",
  sbe: "SBE",
  tl_attendee: "T+L",
  speaker: "Speaker",
  da_staff: "DA Staff",
};

export default function CheckIn() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [q, setQ] = useState("");
  const [scan, setScan] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [showCheckedIn, setShowCheckedIn] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const scanRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) { nav("/login"); return; }
    load();
    // Focus scan input on mount so barcode scanners just work.
    setTimeout(() => scanRef.current?.focus(), 200);
  }, [user, loading]);

  const load = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/attendees");
      setRows(data.attendees || []);
    } catch { toast.error("Could not load attendees."); }
    finally { setLoadingList(false); }
  };

  const doCheckin = async (r, undo = false) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post("/admin/attendees/checkin", {
        type: r.type, id: r.id, checked_in: !undo && !r.checked_in,
      });
      const a = data.attendee;
      setRows((cur) => cur.map((x) => x.id === r.id
        ? { ...x, checked_in: !!a.checked_in, checked_in_at: a.checked_in_at, checked_in_by: a.checked_in_by }
        : x));
      if (!undo && !r.checked_in) {
        toast.success(`${r.name} checked in.`);
        setLastChecked({ ...r, checked_in: true, checked_in_at: a.checked_in_at, checked_in_by: a.checked_in_by });
      } else {
        toast.success("Check-in reversed.");
      }
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Check-in failed.");
    } finally {
      setBusyId(null);
    }
  };

  // Scan handler — accepts full Check-In ID (UUID) or first 8 chars (e.g. as shown in Admin table).
  const submitScan = async (e) => {
    e.preventDefault();
    const term = scan.trim();
    if (!term) return;
    const matches = rows.filter((r) =>
      r.check_in_id && (r.check_in_id === term || r.check_in_id.slice(0, 8).toLowerCase() === term.toLowerCase()));
    if (matches.length === 0) {
      toast.error("No attendee matches that Check-In ID.");
    } else if (matches.length > 1) {
      toast.error("Multiple matches — please refine.");
    } else {
      const r = matches[0];
      if (r.checked_in) toast.info(`${r.name} is already checked in.`);
      else await doCheckin(r);
    }
    setScan("");
    scanRef.current?.focus();
  };

  const filtered = useMemo(() => {
    let list = rows;
    if (!showCheckedIn) list = list.filter((r) => !r.checked_in);
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      list = list.filter((r) =>
        (r.name || "").toLowerCase().includes(t)
        || (r.email || "").toLowerCase().includes(t)
        || (r.company || "").toLowerCase().includes(t)
        || (r.check_in_id || "").toLowerCase().includes(t)
        || (r.check_in_id || "").slice(0, 8).toLowerCase().includes(t)
      );
    }
    return list;
  }, [rows, q, showCheckedIn]);

  const counts = useMemo(() => {
    const total = rows.length;
    const checkedIn = rows.filter((r) => r.checked_in).length;
    return { total, checkedIn, remaining: total - checkedIn };
  }, [rows]);

  if (loading) return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;

  return (
    <Layout>
      <section className="bg-[#0A0A0A] text-white border-b border-white/10">
        <div className="container-summit py-10 md:py-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline text-white/60"><span className="accent-rule" />Event-Day Check-In</div>
              <h1 className="serif text-4xl sm:text-5xl mt-3">Strategic Supplier Summit</h1>
              <p className="mt-3 text-white/70 text-sm">
                Operator: <span className="text-white">{user?.email}</span>
              </p>
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10">
              <Kpi label="Total" value={counts.total} />
              <Kpi label="Checked In" value={counts.checkedIn} accent />
              <Kpi label="Remaining" value={counts.remaining} />
            </div>
          </div>

          {/* Scan bar — auto-focused, submits on Enter */}
          <form onSubmit={submitScan} className="mt-8 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <ScanLine size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A39171]" />
              <Input
                ref={scanRef}
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                placeholder="Scan Check-In ID or paste UUID / first 8 characters and press Enter"
                data-testid="checkin-scan-input"
                className="h-14 rounded-none pl-11 bg-white text-black text-lg tabular-nums"
              />
            </div>
            <button type="submit" className="btn-black h-14 px-8 bg-[#A39171] hover:bg-[#8b7a5f] text-white border-[#A39171]"
                    data-testid="checkin-scan-submit">
              <CheckCircle2 size={16} /> Check In
            </button>
          </form>

          {lastChecked && (
            <div className="mt-6 border border-emerald-400/40 bg-emerald-500/10 p-4 flex items-start gap-3" data-testid="checkin-last-toast">
              <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-medium">{lastChecked.name}</div>
                <div className="text-white/60 text-xs mt-0.5">
                  {TYPE_LABELS[lastChecked.type] || lastChecked.type}
                  {lastChecked.company ? ` · ${lastChecked.company}` : ""}
                  {" · "}
                  Checked in {lastChecked.checked_in_at ? new Date(lastChecked.checked_in_at).toLocaleTimeString() : "just now"}
                </div>
              </div>
              <button onClick={() => setLastChecked(null)} className="text-white/50 hover:text-white" aria-label="Dismiss">
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="container-summit py-8">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[280px] max-w-xl">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, company, email, or Check-In ID"
              data-testid="checkin-search"
              className="h-11 rounded-none pl-10"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-600 cursor-pointer">
            <input type="checkbox" checked={showCheckedIn}
              onChange={(e) => setShowCheckedIn(e.target.checked)}
              data-testid="checkin-show-checked" />
            Show already checked-in
          </label>
          <button onClick={load} className="ml-auto h-11 px-4 text-xs uppercase tracking-[0.18em] border border-zinc-300 hover:border-black inline-flex items-center gap-2"
                  data-testid="checkin-refresh">
            <RefreshCcw size={12} /> Refresh
          </button>
        </div>

        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left">
                <tr className="border-b border-zinc-200">
                  <Th>Type</Th><Th>Name</Th><Th>Company</Th><Th>Contact</Th>
                  <Th>Check-In ID</Th><Th>Status</Th><Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {loadingList && <tr><td colSpan={7} className="p-10 text-center text-zinc-500">Loading attendees…</td></tr>}
                {!loadingList && filtered.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-zinc-500">
                    {rows.length === 0 ? "No attendees registered yet." : "No matches — try a different term or toggle 'Show already checked-in'."}
                  </td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={`${r.type}-${r.id}`} className={`border-b border-zinc-100 last:border-0 ${r.checked_in ? "bg-emerald-50/40" : ""}`}
                      data-testid={`checkin-row-${r.id}`}>
                    <Td><span className="pill">{TYPE_LABELS[r.type] || r.type}</span></Td>
                    <Td>
                      <div className="font-medium flex items-center gap-2">
                        <User size={12} className="text-zinc-400" /> {r.name || "—"}
                      </div>
                      {r.title && <div className="text-xs text-zinc-500 mt-0.5">{r.title}</div>}
                    </Td>
                    <Td>{r.company || <span className="text-zinc-400">—</span>}</Td>
                    <Td>
                      <div className="text-xs">{r.email}</div>
                      {r.phone && <div className="text-[11px] text-zinc-500">{r.phone}</div>}
                    </Td>
                    <Td>
                      <span className="text-[11px] tabular-nums font-mono">
                        {r.check_in_id ? r.check_in_id.slice(0, 8).toUpperCase() : "—"}
                      </span>
                    </Td>
                    <Td>
                      {r.checked_in ? (
                        <div>
                          <div className="inline-flex items-center gap-1 text-emerald-700 text-[11px] uppercase tracking-[0.18em]">
                            <CheckCircle2 size={12} /> Checked in
                          </div>
                          {r.checked_in_at && (
                            <div className="text-[10px] text-zinc-500 mt-1 inline-flex items-center gap-1">
                              <Clock size={10} /> {new Date(r.checked_in_at).toLocaleTimeString()}
                            </div>
                          )}
                          {r.checked_in_by && (
                            <div className="text-[10px] text-zinc-400 mt-0.5">by {r.checked_in_by}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Not checked in</span>
                      )}
                    </Td>
                    <Td>
                      {r.checked_in ? (
                        <button
                          onClick={() => doCheckin(r, true)}
                          disabled={busyId === r.id}
                          data-testid={`checkin-undo-${r.id}`}
                          className="px-3 py-1 text-xs uppercase tracking-[0.18em] border border-zinc-300 hover:border-black">
                          {busyId === r.id ? "…" : "Undo"}
                        </button>
                      ) : (
                        <button
                          onClick={() => doCheckin(r)}
                          disabled={busyId === r.id}
                          data-testid={`checkin-btn-${r.id}`}
                          className="px-4 py-1 text-xs uppercase tracking-[0.18em] bg-black text-white border border-black hover:bg-[#A39171] hover:border-[#A39171]">
                          {busyId === r.id ? "…" : "Check In"}
                        </button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`px-6 py-4 bg-black ${accent ? "text-[#A39171]" : "text-white"}`}>
      <div className="serif text-3xl tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] mt-1 text-white/60">{label}</div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-xs uppercase tracking-[0.18em] text-zinc-500 px-4 py-3">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-4 align-top">{children}</td>;
